import { TextRun, UnderlineType } from 'docx'

/**
 * Process inline markdown formatting (bold, italic, links, code, strikethrough, etc.)
 */
export function processInlineFormatting(text: string, font: string, fontSize: number): TextRun[] {
  const runs: TextRun[] = []
  // Remove trailing backslash (markdown hard line break syntax from tiptap-markdown)
  let currentText = text.replace(/\\$/, '')

  // Regular expression to match various inline elements
  const inlineRegex = /(\*\*.*?\*\*|\*.*?\*|`.*?`|\[.*?\]\(.*?\)|~~.*?~~|==.*?==|\^.*?\^|~.*?~|<u>.*?<\/u>)/g

  let lastIndex = 0
  let match

  while ((match = inlineRegex.exec(currentText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = currentText.substring(lastIndex, match.index)
      if (beforeText) {
        runs.push(new TextRun({ text: beforeText, font, size: fontSize, color: '000000' }))
      }
    }

    const matchedText = match[0]

    // Handle different inline formats
    if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
      // Bold text
      const boldText = matchedText.slice(2, -2)
      runs.push(new TextRun({ text: boldText, bold: true, font, size: fontSize, color: '000000' }))
    } else if (matchedText.startsWith('*') && matchedText.endsWith('*') && !matchedText.startsWith('**')) {
      // Italic text
      const italicText = matchedText.slice(1, -1)
      runs.push(new TextRun({ text: italicText, italics: true, font, size: fontSize, color: '000000' }))
    } else if (matchedText.startsWith('`') && matchedText.endsWith('`')) {
      // Inline code
      const codeText = matchedText.slice(1, -1)
      runs.push(new TextRun({
        text: codeText,
        font: 'Courier New',
        size: fontSize - 2,
        color: '000000'
      }))
    } else if (matchedText.startsWith('[') && matchedText.includes('](')) {
      // Links
      const linkMatch = matchedText.match(/\[(.*?)\]\((.*?)\)/)
      if (linkMatch) {
        const linkText = linkMatch[1]
        const linkUrl = linkMatch[2]
        runs.push(new TextRun({
          text: linkText,
          font,
          size: fontSize,
          color: '000000',
          underline: {
            type: UnderlineType.SINGLE
          }
        }))
      }
    } else if (matchedText.startsWith('~~') && matchedText.endsWith('~~')) {
      // Strikethrough
      const strikeText = matchedText.slice(2, -2)
      runs.push(new TextRun({
        text: strikeText,
        strike: true,
        font,
        size: fontSize,
        color: '000000'
      }))
    } else if (matchedText.startsWith('==') && matchedText.endsWith('==')) {
      // Highlight (converted to bold since we want all black colors)
      const highlightText = matchedText.slice(2, -2)
      runs.push(new TextRun({
        text: highlightText,
        bold: true,
        font,
        size: fontSize,
        color: '000000'
      }))
    } else if (matchedText.startsWith('^') && matchedText.endsWith('^')) {
      // Superscript
      const superText = matchedText.slice(1, -1)
      runs.push(new TextRun({
        text: superText,
        superScript: true,
        font,
        size: fontSize - 4,
        color: '000000'
      }))
    } else if (matchedText.startsWith('~') && matchedText.endsWith('~')) {
      // Subscript
      const subText = matchedText.slice(1, -1)
      runs.push(new TextRun({
        text: subText,
        subScript: true,
        font,
        size: fontSize - 4,
        color: '000000'
      }))
    } else if (matchedText.startsWith('<u>') && matchedText.endsWith('</u>')) {
      // Underline (using HTML-like syntax for underline)
      const underlineText = matchedText.slice(3, -4)
      runs.push(new TextRun({
        text: underlineText,
        underline: {
          type: UnderlineType.SINGLE
        },
        font,
        size: fontSize,
        color: '000000'
      }))
    } else {
      // Fallback - treat as regular text
      runs.push(new TextRun({ text: matchedText, font, size: fontSize, color: '000000' }))
    }

    lastIndex = match.index + matchedText.length
  }

  // Add remaining text after last match
  if (lastIndex < currentText.length) {
    const remainingText = currentText.substring(lastIndex)
    if (remainingText) {
      runs.push(new TextRun({ text: remainingText, font, size: fontSize, color: '000000' }))
    }
  }

  // If no matches found, return the whole text as a single run
  if (runs.length === 0) {
    runs.push(new TextRun({ text: currentText, font, size: fontSize, color: '000000' }))
  }

  return runs
}
